import { AfterViewInit, Directive, ElementRef, HostListener } from '@angular/core';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Traps Tab/Shift+Tab focus within the host element. Re-queries on every keypress to handle dynamic content. */
@Directive({ selector: '[edlFocusTrap]' })
export class FocusTrapDirective implements AfterViewInit {
  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    // When nested inside another [edlFocusTrap], defer to the outer trap.
    if (this.isNested()) return;

    setTimeout(() => {
      // Elements marked [data-skip-initial-focus] (e.g. popover/tooltip triggers whose
      // focus:blur handler would open the panel unexpectedly on modal open) are excluded
      // here only — they remain normal Tab stops via onKeyDown()'s unfiltered query below.
      const focusable = Array.from(
        this.el.nativeElement.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter(el => el.offsetParent !== null && !el.hasAttribute('data-skip-initial-focus'));
      if (focusable.length >= 1) {
        focusable[0].focus();
      }
    }, 0);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    // When nested, let the outer trap handle Tab so focus is not confined to this sub-section.
    if (this.isNested()) return;

    const focusable = Array.from(
      this.el.nativeElement.querySelectorAll<HTMLElement>(FOCUSABLE)
    ).filter(el => el.offsetParent !== null);

    if (focusable.length < 2) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private isNested(): boolean {
    return !!this.el.nativeElement.parentElement?.closest('[edlFocusTrap]');
  }
}
